import React from 'react';
import styled from 'styled-components';

import { tokens } from 'containers/Intelligence/theme';
import { scaledPx } from 'containers/Intelligence/theme/scale';

import { hiddenRowCount, IListViewMore, inlineRowsFor, ListViewMoreFooter } from '../ListViewMore';
import { formatSharePercent } from '../formatMetric';

export type LegendTableDensity = 'compact' | 'default';

export interface ILegendTableRow {
  /** A `tokens.color` value — the same colour the readout paints this category with. */
  color: string;
  label: string;
  /** Pre-formatted, so the legend reads exactly as the readout it labels. */
  value: string;
  /** Share of the whole as a 0–1 fraction; absent means the share was never measured. */
  pct?: number;
}

export interface ILegendTableProps {
  rows: ILegendTableRow[];
  density?: LegendTableDensity;
  /**
   * The swatch column. Off where the readout the legend labels is already one-colour-per-column and
   * a dot would key nothing - the Customer Base coverage rings draw their legend without one.
   */
  dots?: boolean;
  /**
   * Rules a hairline under every row, the last one included. The Customer Base coverage rings draw
   * their legend as a ruled table (`589-29436`); every other legend on the page is a plain list, so
   * this is opt-in rather than a change to the shared treatment.
   */
  dividers?: boolean;
  /**
   * Caps the legend at six rows and moves the rest behind a `View more` overlay. Omitted, the legend
   * draws every row it is handed, exactly as before: the overlay is titled by the block's own
   * heading, which a row array does not carry. PLA-8844.
   */
  viewMore?: IListViewMore;
  className?: string;
}

const DOT_SIZE = 8;

const ROW_SPACING: Record<LegendTableDensity, string> = {
  compact: tokens.space['4'],
  default: tokens.space['8']
};

const RowList = styled.ul<{ $density: LegendTableDensity }>`
  margin: 4px 0 4px 0;
  padding: 0;
  list-style: none;

  /* Safari < 14.1 has no flex gap (conventions/components/cross-browser.md § 5). */
  > * + * {
    margin-top: ${({ $density }) => ROW_SPACING[$density]};
  }
`;

/* 25px row pitch in the ruled variant: a 17px label line, 4px of air under it, the 1px rule, and
   the list's own 4px row spacing. Measured off `589-29436`, where the rules sit 25px apart. */
const ROW_PADDING = tokens.space['4'];

const Row = styled.li<{ $divided: boolean }>`
  display: flex;
  align-items: center;
  ${({ $divided }) =>
    $divided
      ? `padding-bottom: ${ROW_PADDING}; border-bottom: 1px solid ${tokens.color['border-subtle']};`
      : ''}
`;

const Dot = styled.span`
  flex: 0 0 auto;
  width: ${scaledPx(DOT_SIZE)};
  height: ${scaledPx(DOT_SIZE)};
  margin-right: ${tokens.space['8']};
  border-radius: ${tokens.radius.pill};
`;

/* PLA-8844: min-width 0 is what keeps the row inside its card. The dot, the value and the percent
   are all flex: 0 0 auto, so the label is the only item that can give - and a flex item's default
   min-width is its content, which means without this the row is as wide as its longest label and
   escapes a narrow card by whatever the difference is. Long category names then wrap rather than
   truncate: a legend row names the series a colour stands for, so a clipped name loses the reading. */
const RowLabel = styled.span`
  flex: 1 1 auto;
  min-width: 0;
  margin-right: ${tokens.space['12']};
  overflow-wrap: anywhere;
  color: ${tokens.color['text-secondary']};
  font-size: ${tokens.type['label-size']};
  font-weight: ${tokens.type['body-weight']};
  line-height: ${tokens.type['label-line']};
`;

const RowValue = styled.span`
  flex: 0 0 auto;
  color: ${tokens.color['text-primary']};
  font-size: ${tokens.type['label-size']};
  font-weight: ${tokens.type['label-weight']};
  line-height: ${tokens.type['label-line']};
`;

const RowPercent = styled.span`
  flex: 0 0 auto;
  margin-left: ${tokens.space['8']};
  color: ${tokens.color['text-muted']};
  font-size: ${tokens.type['label-size']};
  font-weight: ${tokens.type['body-weight']};
  line-height: ${tokens.type['label-line']};
`;

const LegendTable: React.FC<ILegendTableProps> = ({
  rows,
  density = 'default',
  dots = true,
  dividers = false,
  viewMore,
  className
}) => {
  const legendRows = Array.isArray(rows) ? rows : [];

  if (legendRows.length === 0) {
    return null;
  }

  // One renderer, called twice: the overlay's rows cannot drift from the card's.
  const drawList = (drawn: readonly ILegendTableRow[]) => (
    <RowList
      $density={density}
      className={className}
      data-density={density}
      data-dots={String(dots)}
      data-dividers={String(dividers)}
      data-testid="legend-table"
    >
      {drawn.map(row => (
        <Row key={row.label} $divided={dividers} data-testid="legend-table-row">
          {dots ? (
            <Dot
              aria-hidden
              style={{ background: row.color }}
              data-color={row.color}
              data-testid="legend-table-dot"
            />
          ) : null}
          <RowLabel data-testid="legend-table-label">{row.label}</RowLabel>
          <RowValue data-testid="legend-table-value">{row.value}</RowValue>
          {/* An absent share was never measured; a `0` share is a real reading and still renders. */}
          {Number.isFinite(row.pct) ? (
            <RowPercent data-testid="legend-table-pct">{formatSharePercent(row.pct)}</RowPercent>
          ) : null}
        </Row>
      ))}
    </RowList>
  );

  return (
    <>
      {drawList(inlineRowsFor(legendRows, viewMore))}
      {hiddenRowCount(legendRows, viewMore) > 0 ? (
        <ListViewMoreFooter
          rows={legendRows}
          viewMore={viewMore}
          renderFull={() => drawList(legendRows)}
        />
      ) : null}
    </>
  );
};

export default LegendTable;
